import Styles from '../../../1.manual/3.school/2.edit/styles.module.css';
import React from 'react';
import { useState } from 'react';
import DefaultInput1 from '../../../../package/1-input/index';
import ErrorMessage from '../../../../package/error/index';
import { getFirestore } from 'firebase/firestore';
import { writeJobPostingBothStores } from '../../jobPostingRef';
import { infoData, PREFECTURES, prefFromAddress, ValidationError } from '../../../../../type';
import DefaultCheckBox from 'src/salon/package/checkBox';
import DefaultSelect1 from 'src/salon/package/1-select';
import Hint from '../../../1.manual/3.school/2.edit/hint';
import DefaultDesc from 'src/salon/package/desc';

type JobPostingsInfoProps = {
  companyId: string;
  year: string;
  setChangeData: React.Dispatch<React.SetStateAction<boolean>>;
  info: infoData,
  setInfo: React.Dispatch<React.SetStateAction<infoData>>;
};

const JobPostingsInfo: React.FC<JobPostingsInfoProps> = ({ companyId, year, setChangeData, info, setInfo }) => {

  const [ errorMessage, setErrorMessage ] = useState<ValidationError>(null);
  const [ banner, setBanner ] = useState<boolean>(false);
  const [ hintText, setHintText ] = useState<any>({ title: "", desc: "" });

  const db = getFirestore();

  function validateCompanyData(data: infoData) : { message: string; fieldId: string } | null {

    // --- 企業名 ---
    if (!data.companyName) return { message: '企業名を入力してください。', fieldId: 'companyName' };
    if (!data.companyNameKana) return { message: '企業名（フリガナ）を入力してください。', fieldId: 'companyNameKana' };
    if (isKatakana(data.companyNameKana)) return { message: '企業名（フリガナ）はカタカナで入力してください。', fieldId: 'companyNameKana' };

    // --- 住所 ---
    if (!data.addressPref) return { message: '本社の都道府県を選択してください。', fieldId: 'addressPref' };
    if (!data.address) return { message: '住所を入力してください。', fieldId: 'address' };
    if (!data.addressKana) return { message: '住所（フリガナ）を入力してください。', fieldId: 'addressKana' };
    if (isKatakana(data.addressKana)) return { message: '住所（フリガナ）はカタカナで入力してください。', fieldId: 'addressKana' };

    // --- 代表者 ---
    if (!data.ownerName) return { message: '代表者を入力してください。', fieldId: 'ownerName' };
    if (hasFullWidthSpace(data.ownerName)) return { message: '代表者の氏名の間に全角スペースを入力してください。', fieldId: 'ownerName' };
    if (!data.ownerNameKana) return { message: '代表者（フリガナ）を入力してください。', fieldId: 'ownerNameKana' };
    if (hasFullWidthSpace(data.ownerNameKana)) return { message: '代表者（フリガナ）の氏名の間に全角スペースを入力してください。', fieldId: 'ownerNameKana' };
    if (isKatakana(data.ownerNameKana)) return { message: '代表者（フリガナ）はカタカナでご入力ください。', fieldId: 'ownerNameKana' };

    // --- 設立・従業員数 ---
    if (!data.establishedDate) return { message: '設立を入力してください。', fieldId: 'establishedDate' };
    if (isNumeric(data.establishedDate)) return { message: '設立を数字で入力してください。', fieldId: 'establishedDate' };
    if (info.establishedDate.length !== 4) return { message: '設立は西暦（2020, 1970など）で入力してください。', fieldId: 'establishedDate' };
    if (!data.numberOfEmployees) return { message: '従業員数を入力してください。', fieldId: 'numberOfEmployees' };
    if (isNumeric(data.numberOfEmployees)) return { message: '従業員数を数字で入力してください。', fieldId: 'numberOfEmployees' };

    // --- 電話番号 ---
    if (!data.phoneNumber) return { message: '電話番号を入力してください。', fieldId: 'phoneNumber' };
    if (isValidPhoneNumber(data.phoneNumber)) return { message: '電話番号を正しく入力してください。', fieldId: 'phoneNumber' };
    if (!info.phoneNumber.includes('-')) return { message: '電話番号にハイフン（-）を付けて、入力してください。', fieldId: 'phoneNumber' };
    if (info.phoneNumber.length <= 11) return { message: '電話番号の桁数が足りないため、見直してください。', fieldId: 'phoneNumber' };

    // --- メール ---
    if (!data.email) return { message: 'メールアドレスを入力してください。', fieldId: 'email' };
    if (isValidEmail(data.email)) return { message: 'メールアドレスの形式に誤りがあります', fieldId: 'email' };

    // --- ホームページ ---
    if (data.homepageUrl && isValidHomepageUrl(data.homepageUrl)) return { message: '正しいホームページURL（https://~）を入力してください。', fieldId: 'homepageUrl' };

    // --- インスタグラム ---
    if (data.instagram && isValidInstagram(data.instagram)) return { message: '正しいアカウント名(@は省略)を入力してください。', fieldId: 'instagram' };
    if (data.instagram && info.instagram.includes("@")) return { message: '@は省略してください。', fieldId: 'instagram' };

    // --- 採用担当者 ---
    if (!data.recruiterName) return { message: '採用担当者を入力してください。', fieldId: 'recruiterName' };
    if (hasFullWidthSpace(data.recruiterName)) return { message: '採用担当者の氏名の間に全角スペースを入力してください。', fieldId: 'recruiterName' };
    if (!data.recruiterNameKana) return { message: '採用担当者（フリガナ）を入力してください。', fieldId: 'recruiterNameKana' };
    if (isKatakana(data.recruiterNameKana)) return { message: '採用担当者（フリガナ）はカタカナで入力してください。', fieldId: 'recruiterNameKana' };
    if (hasFullWidthSpace(data.recruiterNameKana)) return { message: '採用担当者（フリガナ）名の間に全角スペースを入力してください。', fieldId: 'recruiterNameKana' };

    // --- チェック項目 ---
    if (data.business.contents.length === 0) return { message: '事業内容にチェックを入れてください。', fieldId: 'business.contents' };
    if (data.business.canConcurrent === null) return { message: '兼任制度の有無を選択してください。', fieldId: 'business.canConcurrent' };
    if (data.isSpecialExemption === null) return { message: '特例措置対象事業所の有無を選択してください。', fieldId: 'isSpecialExemption' };
    if (data.exist36Agreement === null) return { message: '36協定の有無を選択してください。', fieldId: 'exist36Agreement' };

    return null;
  }

  // ---------- 共通バリデーション関数 ----------
  const isNumeric = (str: string) => !!str && !/^[0-9]+$/.test(str);
  const isKatakana = (str: string) => !!str && !/^[ァ-ヶー　]+$/.test(str);
  const hasFullWidthSpace = (str: string) => !!str && !/　/.test(str);
  const isKatakanaOrFullWidthSpace = (str: string) => !!str && !/^[ァ-ヶー　]+$/.test(str);
  const isValidPhoneNumber = (str: string) => !!str && !/^[0-9-]{9,}$/.test(str);
  const isValidEmail = (str: string) => !!str && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(str);
  const isValidHomepageUrl = (str: string) => !!str && !/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/.test(str);
  const isValidInstagram = (str: string) => !!str && !/^(?!.*\.\.)(?!.*\.$)[a-zA-Z0-9._]{1,30}$/.test(str);

  function check() {
    setErrorMessage(null);
    window.scrollTo(0, 0);
    const error = validateCompanyData(info);
    if (error) {
      setErrorMessage(error);

      const el = document.getElementById(error.fieldId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus?.();
      }
      return;
    }
    save(true);
  }

  const [ uploading, setUploading ] = useState(false);

  async function save(isCompleted: boolean) {
    try {
      setUploading(true);

      const businessToRecruitField: Record<string, string> = {
        "理容": "recruitsBarber",
        "美容": "recruitsBeauty",
        "メイク": "recruitsMake",
        "アイ": "recruitsEye",
        "ネイル": "recruitsNeil",
        "ブライダル": "recruitsBridal",
        "エステ": "recruitsAesthetic",
      };

      const clearedRecruits: Record<string, string> = {};
      Object.entries(businessToRecruitField).forEach(([business, field]) => {
        if (!info.business.contents.includes(business)) {
          clearedRecruits[`applicationInfo.${field}`] = "";
        }
      });

      await writeJobPostingBothStores(db, companyId, year, {
        info: {
          ...info,
          isCompleted,
          time: new Date(),
        },
        ...clearedRecruits,
      });

      setChangeData(false);

    } catch (e) {
      setUploading(false);
      console.error(e);
    } finally {
      setUploading(false);
      setBanner(true);
      setTimeout(() => setBanner(false), 1500);
    }
  }

  return (
    <>
    {errorMessage && <ErrorMessage message={errorMessage.message} />}
    {banner && <div className={Styles.banner}>保存しました。</div>}
    {hintText.title !== "" && ( <Hint contents={hintText} setHintText={setHintText} /> )}

    {uploading && (
      <div className={Styles.lockOverlay}>
        <div className={Styles.spinner} />
        <p>アップロード中...</p>
      </div>
    )}

    <div className={Styles.title_edit}>
      <h4>企業情報の編集</h4>
      <div className={Styles.title_edit_btn}>
        <button className={Styles.save_box_now} onClick={() => save(false)}>一時保存</button>
        <button className={Styles.save_box_save} onClick={() => check()}>保存</button>
      </div>
    </div>

    <div className={Styles.test_box}>

      <div id="companyName" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "companyName"}
        title="企業名" length={25} place="株式会社memorii" value={info.companyName} desc=""
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, companyName: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}
        hint={{ title: "", desc: "" }}
        setHintText={setHintText} />

      <div id="companyNameKana" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "companyNameKana"}
        title={'企業名（フリガナ）'} length={25} place={'カブシキガイシャメモリー'}
        value={info.companyNameKana}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, companyNameKana: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>
      <DefaultDesc text={"※企業名（フリガナ）はカタカナで入力してください。"} type={"error"} error={isKatakana(info.companyNameKana)} />

    </div>

    <div className={Styles.test_box}>

      <div id="addressPref" tabIndex={-1}></div>
      <DefaultSelect1
        error={errorMessage?.fieldId === "addressPref"}
        title={'都道府県'} list={['未選択', ...PREFECTURES]}
        value={info.addressPref}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, addressPref: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>
      <DefaultDesc text={"※本社の都道府県を選択してください。アプリの求人一覧に表示されます。"} />

      <div id="address" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "address"}
        title={'住所'} length={25} place={'神奈川県横浜市中区本町~~'}
        value={info.address}
        setValue={(val: string) => {
          // 都道府県が未選択なら、住所の先頭の都道府県を入れる
          setInfo((prev) => ({ ...prev, address: val, addressPref: prev.addressPref || prefFromAddress(val) }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>

      <div id="addressKana" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "addressKana"}
        title={'住所（フリガナ）'} length={25} place={'カナガワケンヨコハマシナカク~~'}
        value={info.addressKana}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, addressKana: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>
      <DefaultDesc text={"※地番以降は省略してください。"} />
      <DefaultDesc text={"※住所(地番以降は省略)はカタカナで入力してください。"} type={"error"} error={isKatakana(info.addressKana)} />

    </div>

    <div className={Styles.test_box}>

      <div id="ownerName" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "ownerName"}
        title={'代表者'} length={25} place={'代表　太郎'}
        value={info.ownerName}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, ownerName: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>
      <DefaultDesc text={"※氏名の間は全角スペースでご入力ください。"} type={"error"} error={hasFullWidthSpace(info.ownerName)} />

      <div id="ownerNameKana" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "ownerNameKana"}
        title={'代表者（フリガナ）'} length={25} place={'ダイヒョウ　タロウ'}
        value={info.ownerNameKana}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, ownerNameKana: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>
      <DefaultDesc text={"※氏名の間は全角スペースでご入力ください。"} type={"error"} error={hasFullWidthSpace(info.ownerNameKana)} />
      <DefaultDesc text={"※代表者（フリガナ）はカタカナでご入力ください。"} type={"error"} error={isKatakanaOrFullWidthSpace(info.ownerNameKana)} />

    </div>

    <div className={Styles.test_box}>

      <div id="establishedDate" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "establishedDate"}
        title={'設立（年）'} length={4} place={'2000'}
        value={info.establishedDate}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, establishedDate: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>
      <DefaultDesc text={"※半角数字で入力してください。"} type={"error"} error={isNumeric(info.establishedDate)} />

      {info.establishedDate && info.establishedDate.length !== 4 && (
        <DefaultDesc text={"※西暦4桁（2020, 1970など）で入力してください。"} type={"error"} error={true} />
      )}

      <div id="numberOfEmployees" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "numberOfEmployees"}
        title={'従業員数（人）'} length={4} place={'12'}
        value={info.numberOfEmployees}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, numberOfEmployees: val }));
          setChangeData(true)
          setErrorMessage(null);
        }}/>
      <DefaultDesc text={"※半角数字で入力してください。"} type={"error"} error={isNumeric(info.numberOfEmployees)} />

    </div>

    <div className={Styles.test_box}>

      <div id="phoneNumber" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "phoneNumber"}
        title={'電話番号'} length={13} place={'090-xxxx-yyyy'}
        value={info.phoneNumber}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, phoneNumber: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>
      <DefaultDesc text={"※半角数字で入力してください。"} type={"error"} error={isValidPhoneNumber(info.phoneNumber)} />

      {info.phoneNumber && !/-/.test(info.phoneNumber) && (
        <DefaultDesc text={"※ハイフンを付けて入力してください。"} type={"error"} error={true} />
      )}

      <div id="email" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "email"}
        title={'メール'} length={40} place={'memorii@memorii-works.com'}
        value={info.email}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, email: val }));
          setErrorMessage(null);
          setChangeData(true);
        }}/>
      {info.email && isValidEmail(info.email) && (
        <DefaultDesc text={"※メールアドレスの形式に誤りがあります"} type={"error"} error={true} />
      )}

      <div id="homepageUrl" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "homepageUrl"}
        title={'ホームページ（任意）'} length={45} place={'https://memorii-works.com'}
        value={info.homepageUrl}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, homepageUrl: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>
      {info.homepageUrl && isValidHomepageUrl(info.homepageUrl) && (
        <DefaultDesc text={"※正しいホームページURL（https://~）を入力してください。"} type={"error"} error={true} />
      )}

      <div id="instagram" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "instagram"}
        title={'インスタグラム（任意）'} length={30} place={'memorii.photo'}
        value={info.instagram} setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, instagram: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>
      <DefaultDesc text={"※正しいアカウント名(@は省略)を入力してください。"} type={"error"} error={isValidInstagram(info.instagram)} />
      {info.instagram && info.instagram.includes("@") && (
        <DefaultDesc text={"※@は省略してください。"} type={"error"} error={true} />
      )}

    </div>

    <div className={Styles.test_box}>

      <div id="recruiterName" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "recruiterName"}
        title={'採用担当者'} length={25} place={'採用　太郎'}
        value={info.recruiterName}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, recruiterName: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>
      <DefaultDesc text={"※氏名の間は全角スペースでご入力ください。"} type={"error"} error={hasFullWidthSpace(info.recruiterName)} />

      <div id="recruiterNameKana" tabIndex={-1}></div>
      <DefaultInput1
        error={errorMessage?.fieldId === "recruiterNameKana"}
        title={'採用担当者（フリガナ）'} length={25} place={'サイヨウ　タロウ'}
        value={info.recruiterNameKana}
        setValue={(val: string) => {
          setInfo((prev) => ({ ...prev, recruiterNameKana: val }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>

      <DefaultDesc text={"※採用担当者（フリガナ）カタカナでご入力ください。"} type={"error"} error={isKatakanaOrFullWidthSpace(info.recruiterNameKana)} />
      <DefaultDesc text={"※氏名の間は全角スペースでご入力ください。"} type={"error"} error={hasFullWidthSpace(info.recruiterNameKana)} />

    </div>

    <div className={Styles.test_box}>

      <div id="business.contents" tabIndex={-1}></div>
      <DefaultCheckBox
        error={errorMessage?.fieldId === "business.contents"}
        title={'事業内容'}
        list={[ "理容", "美容", "メイク", "ネイル", "アイ", "ブライダル", "エステ", "その他" ]}
        value={info.business.contents}
        setValue={(val: string[]) => {
          setInfo((prev) => ({ ...prev, business: { ...prev.business, contents: val } }));
          setChangeData(true);
          setErrorMessage(null);
        }} />

      <div id="business.canConcurrent" tabIndex={-1}></div>
      <DefaultSelect1
        error={errorMessage?.fieldId === "business.canConcurrent"}
        title={'複数事業の兼任（理容と美容、ヘアとネイル等）'}
        list={['未選択', "兼任可能", "兼任不可"]}
        value={info.business.canConcurrent === null ? '未選択' : info.business.canConcurrent ? "兼任可能" : "兼任不可"}
        setValue={(val: string) => {
          setInfo(prev => ({ ...prev, business: { ...prev.business, canConcurrent: val === "兼任可能" ? true : val === "兼任不可" ? false : null } }));
          setChangeData(true);
          setErrorMessage(null);
        }} />

    </div>

    <div className={Styles.test_box}>

      <div id="specialExemption" tabIndex={-1}></div>
      <DefaultSelect1
        error={errorMessage?.fieldId === "specialExemption"}
        title={'特例措置対象事業所の有無'} list={[ '未選択', "特例措置対象事業所である", "特例措置対象事業所ではない" ]}
        value={info.isSpecialExemption === null ? '未選択' : info.isSpecialExemption ? "特例措置対象事業所である" : "特例措置対象事業所ではない"}
        setValue={(val: string) => {
          setInfo(prev => ({ ...prev, isSpecialExemption: val === "特例措置対象事業所である" ? true : val === "特例措置対象事業所ではない" ? false : null }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>
      <DefaultDesc text={"※常時10人未満の労働者を使用する「保健衛生業（理美容室を含む）」に該当する事業所は特例措置対象事業所となる。"} />

      <div id="exist36Agreement" tabIndex={-1}></div>
      <DefaultSelect1
        error={errorMessage?.fieldId === "exist36Agreement"}
        title={'36協定の有無'} list={[ '未選択', "36協定を提出済み", "36協定を未提出" ]}
        value={info.exist36Agreement === null ? '未選択' : info.exist36Agreement ? "36協定を提出済み" : "36協定を未提出"}
        setValue={(val: string) => {
          setInfo(prev => ({ ...prev, exist36Agreement: val === "36協定を提出済み" ? true : val === "36協定を未提出" ? false : null }));
          setChangeData(true);
          setErrorMessage(null);
        }}/>

    </div>

    </>
  );
}

export default JobPostingsInfo;
